import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Shield, Mail, Lock, User, Phone, ArrowRight, AlertCircle, CheckCircle2, Gift } from "lucide-react";
import { referralsAPI } from "../lib/api";
import { toast } from "sonner";

const REF_STORAGE_KEY = "creditfix_ref";

const RegisterPage = () => {
    const navigate = useNavigate();
    const { register } = useAuth();
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [referralInfo, setReferralInfo] = useState(null);
    const [refCode, setRefCode] = useState("");
    const [formData, setFormData] = useState({
        fullName: "",
        email: "",
        phone: "",
        password: "",
        confirmPassword: ""
    });

    const benefits = [
        "Free credit report analysis",
        "AI-powered issue detection",
        "Preview dispute letters",
        "Real-time SMS dispute alerts",
        "FCRA compliance guaranteed"
    ];

    const normalizePhone = (raw) => {
        const trimmed = (raw || "").trim();
        if (!trimmed) return "";
        if (trimmed.startsWith("+")) return "+" + trimmed.slice(1).replace(/\D/g, "");
        const digits = trimmed.replace(/\D/g, "");
        // Default to US country code if 10 digits
        return digits.length === 10 ? `+1${digits}` : `+${digits}`;
    };

    const isValidE164 = (p) => /^\+[1-9]\d{7,14}$/.test(p);

    // Capture ?ref=CODE from the URL, validate against backend, and persist for the
    // user across navigation (so they can browse the marketing pages and still get
    // the discount attached at signup).
    useEffect(() => {
        const fromUrl = searchParams.get("ref");
        const stored = localStorage.getItem(REF_STORAGE_KEY);
        const candidate = (fromUrl || stored || "").trim().toUpperCase();
        if (!candidate) return;

        let isMounted = true;
        (async () => {
            try {
                const r = await referralsAPI.info(candidate);
                if (!isMounted) return;
                setReferralInfo(r.data);
                setRefCode(candidate);
                localStorage.setItem(REF_STORAGE_KEY, candidate);
            } catch {
                // Invalid code — clear it silently so the user doesn't see a bogus banner
                if (isMounted) {
                    localStorage.removeItem(REF_STORAGE_KEY);
                    setRefCode("");
                }
            }
        })();
        return () => { isMounted = false; };
    }, [searchParams]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");

        if (formData.password !== formData.confirmPassword) {
            setError("Passwords do not match");
            return;
        }

        if (formData.password.length < 6) {
            setError("Password must be at least 6 characters");
            return;
        }

        const phoneE164 = normalizePhone(formData.phone);
        if (!isValidE164(phoneE164)) {
            setError("Please enter a valid phone number with country code (e.g. +14155552671)");
            return;
        }

        setLoading(true);

        try {
            await register(formData.email, formData.password, formData.fullName, phoneE164, refCode || null);
            if (refCode) localStorage.removeItem(REF_STORAGE_KEY);
            toast.success(referralInfo?.discount_percent
                ? `Account created! Your ${referralInfo.discount_percent}% referral discount is ready at checkout.`
                : "Account created! Watch for a welcome text.");
            navigate("/dashboard");
        } catch (err) {
            const message = err.response?.data?.detail || "Registration failed";
            setError(typeof message === "string" ? message : "Registration failed");
            toast.error(typeof message === "string" ? message : "Registration failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-background flex">
            {/* Left Side - Benefits */}
            <div className="hidden lg:flex flex-1 bg-primary items-center justify-center p-12">
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="max-w-lg text-primary-foreground"
                >
                    <h2 className="text-3xl font-serif font-bold mb-6">
                        Start Your Credit Repair Journey Today
                    </h2>
                    <p className="text-lg opacity-90 mb-8">
                        Join thousands who have improved their credit scores with our 
                        AI-powered analysis and professionally crafted dispute letters.
                    </p>
                    
                    <div className="space-y-4">
                        {benefits.map((benefit, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.1 * (index + 1) }}
                                className="flex items-center gap-3"
                            >
                                <CheckCircle2 className="h-5 w-5 text-accent" />
                                <span>{benefit}</span>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>
            </div>

            {/* Right Side - Form */}
            <div className="flex-1 flex items-center justify-center p-8">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="w-full max-w-md"
                >
                    <Link to="/" className="flex items-center gap-2 mb-8">
                        <Shield className="h-8 w-8 text-primary" />
                        <span className="font-serif text-xl font-semibold">CreditFix AI</span>
                    </Link>

                    <Card className="border-border shadow-soft">
                        <CardHeader className="space-y-1">
                            <CardTitle className="text-2xl font-serif">Create an account</CardTitle>
                            <CardDescription>
                                Get started with your free credit analysis
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                {referralInfo?.discount_percent > 0 && (
                                    <div
                                        className="flex items-start gap-2 p-3 rounded-lg bg-accent/10 border border-accent/30 text-sm"
                                        data-testid="referral-banner"
                                    >
                                        <Gift className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                                        <div>
                                            <p className="font-medium text-foreground">
                                                {referralInfo.referrer_first_name} invited you — {referralInfo.discount_percent}% off your first checkout
                                            </p>
                                            <p className="text-xs text-muted-foreground">
                                                Applies automatically to your plan or letter mailing at checkout.
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {error && (
                                    <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                                        <AlertCircle className="h-4 w-4" />
                                        {error}
                                    </div>
                                )}

                                <div className="space-y-2">
                                    <Label htmlFor="fullName">Full Name</Label>
                                    <div className="relative">
                                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                        <Input
                                            id="fullName"
                                            type="text"
                                            placeholder="John Doe"
                                            className="pl-10"
                                            value={formData.fullName}
                                            onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                                            required
                                            data-testid="register-name-input"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="email">Email</Label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                        <Input
                                            id="email"
                                            type="email"
                                            placeholder="you@example.com"
                                            className="pl-10"
                                            value={formData.email}
                                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                            required
                                            data-testid="register-email-input"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="phone">Mobile Phone (for dispute alerts)</Label>
                                    <div className="relative">
                                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                        <Input
                                            id="phone"
                                            type="tel"
                                            placeholder="+1 (415) 555-2671"
                                            className="pl-10"
                                            value={formData.phone}
                                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                            required
                                            data-testid="register-phone-input"
                                        />
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                        We'll text you when dispute statuses change. You can opt out anytime.
                                    </p>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="refCode" className="flex items-center gap-1.5">
                                        Referral code <span className="text-xs text-muted-foreground font-normal">(optional)</span>
                                    </Label>
                                    <div className="relative">
                                        <Gift className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                        <Input
                                            id="refCode"
                                            type="text"
                                            placeholder="e.g. ABCD2345"
                                            className="pl-10 uppercase font-mono tracking-wider"
                                            value={refCode}
                                            onChange={(e) => setRefCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8))}
                                            data-testid="register-referral-input"
                                            maxLength={8}
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="password">Password</Label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                        <Input
                                            id="password"
                                            type="password"
                                            placeholder="••••••••"
                                            className="pl-10"
                                            value={formData.password}
                                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                            required
                                            data-testid="register-password-input"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="confirmPassword">Confirm Password</Label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                        <Input
                                            id="confirmPassword"
                                            type="password"
                                            placeholder="••••••••"
                                            className="pl-10"
                                            value={formData.confirmPassword}
                                            onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                                            required
                                            data-testid="register-confirm-password-input"
                                        />
                                    </div>
                                </div>

                                <Button 
                                    type="submit" 
                                    className="w-full rounded-full btn-press"
                                    disabled={loading}
                                    data-testid="register-submit-btn"
                                >
                                    {loading ? (
                                        <span className="flex items-center gap-2">
                                            <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                                            Creating account...
                                        </span>
                                    ) : (
                                        <span className="flex items-center gap-2">
                                            Create Account
                                            <ArrowRight className="h-4 w-4" />
                                        </span>
                                    )}
                                </Button>
                            </form>

                            <div className="mt-6 text-center text-sm">
                                <span className="text-muted-foreground">Already have an account? </span>
                                <Link 
                                    to="/login" 
                                    className="text-primary font-medium hover:underline"
                                    data-testid="login-link"
                                >
                                    Sign in
                                </Link>
                            </div>
                        </CardContent>
                    </Card>
                </motion.div>
            </div>
        </div>
    );
};

export default RegisterPage;
